'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["ownerState", "partProps", "resolveBuiltInPartRenderer", "afterContent", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChatPartRenderer } from "../hooks/useChatPartRenderer.js";
import { useChatOnToolCall } from "../hooks/useChatOnToolCall.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { useMessageContext } from "./internals/MessageContext.js";
import { FilePart } from "./parts/FilePart.js";
import { ReasoningPart } from "./parts/ReasoningPart.js";
import { SourceDocumentPart } from "./parts/SourceDocumentPart.js";
import { SourceUrlPart } from "./parts/SourceUrlPart.js";
import { ToolPart } from "./parts/ToolPart.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function DefaultPartFallback(props) {
  const {
    part
  } = props;
  return /*#__PURE__*/_jsx("div", {
    "data-part-type": part.type
  });
}
function TextPart(props) {
  const {
    text,
    renderText
  } = props;
  const rendered = React.useMemo(() => renderText(text), [text, renderText]);
  return /*#__PURE__*/_jsx(React.Fragment, {
    children: rendered
  });
}
function JsonBlock(props) {
  const {
    value
  } = props;
  return /*#__PURE__*/_jsx("pre", {
    children: JSON.stringify(value, null, 2)
  });
}
const renderDefaultStepStartPart = () => /*#__PURE__*/_jsx("div", {
  role: "separator"
});
if (process.env.NODE_ENV !== "production") renderDefaultStepStartPart.displayName = "renderDefaultStepStartPart";
const renderDefaultDataPart = ({
  part
}) => /*#__PURE__*/_jsx(JsonBlock, {
  value: part.data
});
if (process.env.NODE_ENV !== "production") renderDefaultDataPart.displayName = "renderDefaultDataPart";
function MessageRenderedPart(props) {
  const {
    part,
    index,
    message,
    partProps,
    resolveBuiltInPartRenderer
  } = props;
  const customRenderer = useChatPartRenderer(part.type);
  const localeText = useChatLocaleText();
  const onToolCall = useChatOnToolCall();

  // Priority 1: User-provided custom renderer from ChatProvider
  if (customRenderer != null) {
    return /*#__PURE__*/_jsx(React.Fragment, {
      children: customRenderer({
        part,
        message,
        index,
        onToolCall
      })
    });
  }

  // Priority 2: Legacy resolveBuiltInPartRenderer callback (deprecated)
  if (resolveBuiltInPartRenderer != null) {
    const builtInRenderer = resolveBuiltInPartRenderer(part, localeText);
    if (builtInRenderer != null) {
      return /*#__PURE__*/_jsx(React.Fragment, {
        children: builtInRenderer({
          part,
          message,
          index,
          onToolCall
        })
      });
    }
  }

  // Priority 3: Built-in unstyled part renderer components with partProps
  const baseProps = {
    part,
    message,
    index,
    onToolCall
  };
  switch (part.type) {
    case 'text':
      if (partProps?.text?.renderText) {
        return /*#__PURE__*/_jsx(TextPart, {
          text: part.text,
          renderText: partProps.text.renderText
        });
      }
      return /*#__PURE__*/_jsx("div", {
        children: part.text
      });
    case 'reasoning':
      return /*#__PURE__*/_jsx(ReasoningPart, _extends({}, partProps?.reasoning, baseProps, {
        part: part
      }));
    case 'tool':
      return /*#__PURE__*/_jsx(ToolPart, _extends({}, partProps?.tool, baseProps, {
        part: part
      }));
    case 'dynamic-tool':
      return /*#__PURE__*/_jsx(ToolPart, _extends({}, partProps?.['dynamic-tool'], baseProps, {
        part: part
      }));
    case 'file':
      return /*#__PURE__*/_jsx(FilePart, _extends({}, partProps?.file, baseProps, {
        part: part
      }));
    case 'source-url':
      return /*#__PURE__*/_jsx(SourceUrlPart, _extends({}, partProps?.['source-url'], baseProps, {
        part: part
      }));
    case 'source-document':
      return /*#__PURE__*/_jsx(SourceDocumentPart, _extends({}, partProps?.['source-document'], baseProps, {
        part: part
      }));
    case 'step-start':
      return /*#__PURE__*/_jsx(React.Fragment, {
        children: renderDefaultStepStartPart(baseProps)
      });
    default:
      if (part.type.startsWith('data-')) {
        return /*#__PURE__*/_jsx(React.Fragment, {
          children: renderDefaultDataPart(baseProps)
        });
      }
      return /*#__PURE__*/_jsx(DefaultPartFallback, {
        part: part
      });
  }
}
export const MessageContent = /*#__PURE__*/React.forwardRef(function MessageContent(props, ref) {
  const _ref = props,
    {
      ownerState: ownerStateProp,
      partProps,
      resolveBuiltInPartRenderer,
      afterContent,
      slots,
      slotProps
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  const ownerState = useMessageContext();
  void ownerStateProp;
  const Content = slots?.content ?? 'div';
  const Bubble = slots?.bubble ?? 'div';
  const message = ownerState.message;
  const contentProps = useSlotProps({
    elementType: Content,
    externalSlotProps: slotProps?.content,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref
    }
  });
  const bubbleProps = useSlotProps({
    elementType: Bubble,
    externalSlotProps: slotProps?.bubble,
    ownerState
  });
  return /*#__PURE__*/_jsx(Content, _extends({}, contentProps, {
    children: /*#__PURE__*/_jsxs(Bubble, _extends({}, bubbleProps, {
      children: [message ? message.parts.map((part, idx) => /*#__PURE__*/_jsx(MessageRenderedPart, {
        part: part,
        index: idx,
        message: message,
        partProps: partProps,
        resolveBuiltInPartRenderer: resolveBuiltInPartRenderer
      }, `${ownerState.messageId}-${idx}-${part.type}`)) : null, afterContent]
    }))
  }));
});
if (process.env.NODE_ENV !== "production") MessageContent.displayName = "MessageContent";